'use client';
import { useValidateEmail } from '@/hooks/useValidateEmail';
import { cn } from '@/lib/cn';
import { Badge } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import { Input, type InputProps } from '@heidi/design-system/input';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import {
  TypographyCaption,
  TypographyCaptionBold,
} from '@heidi/design-system/typography';
import { TriangleAlert, X } from 'lucide-react';
import { forwardRef, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

type InputEmailsProps = Omit<InputProps, 'value' | 'onChange'> & {
  value: string[];
  onChange: React.Dispatch<React.SetStateAction<string[]>>;
  placeholder?: string;
  limit?: number;
  onValidChange?: (isValid: boolean) => void;
  failedEmailsData?: Record<string, string>;
};
const InputEmails = forwardRef<HTMLInputElement, InputEmailsProps>(
  (
    {
      className,
      value,
      onChange,
      limit,
      placeholder,
      onValidChange,
      failedEmailsData,
      ...props
    },
    ref
  ) => {
    const [pendingDataPoint, setPendingDataPoint] = useState('');
    const isLimitReached = limit && value.length >= limit;
    const intl = useIntl();

    // Email validation regex
    const { isValidEmail } = useValidateEmail();
    const invalidEmails = value.filter((email) => !isValidEmail(email));
    const isValid = value.length > 0 && invalidEmails.length === 0;

    useEffect(() => {
      if (typeof onValidChange === 'function') {
        onValidChange(isValid);
      }
    }, [isValid, onValidChange]);

    useEffect(() => {
      if (pendingDataPoint.includes(',')) {
        const newDataPoints = new Set([
          ...value,
          ...pendingDataPoint.split(',').map((chunk) => chunk.trim()),
        ]);
        onChange(Array.from(newDataPoints));
        setPendingDataPoint('');
      }
    }, [pendingDataPoint, onChange, value]);

    const addPendingDataPoint = () => {
      if (pendingDataPoint) {
        const newDataPoints = new Set([...value, pendingDataPoint.trim()]);
        onChange(Array.from(newDataPoints));
        setPendingDataPoint('');
      }
    };

    return (
      <div className="w-full">
        <div
          className={cn(
            'flex max-h-[150px] min-h-9 w-full flex-wrap gap-2 overflow-auto rounded-md border bg-surface px-3 py-2 text-sm ring-offset-white disabled:cursor-not-allowed disabled:opacity-50',
            isLimitReached
              ? 'border-validation-destructive focus-within:border-border-focus'
              : 'border-border',
            className
          )}
        >
          {value.map((item) => {
            const isInvalid = !isValidEmail(item.trim());
            const isFailed = failedEmailsData?.[item];
            return (
              <Tooltip key={item}>
                <TooltipTrigger>
                  <Badge
                    color={isInvalid ? 'red' : isFailed ? 'orange' : 'neutral'}
                    className={cn('flex items-center gap-1')}
                    size="sm"
                  >
                    {isInvalid && (
                      <TriangleAlert size={12} className="text-validation-destructive" />
                    )}
                    {item}
                    <Button
                      disableAutomaticSvgSizing
                      variant="ghost"
                      className={cn(
                        'size-3 p-0 text-text-secondary',
                        isInvalid &&
                          'text-validation-destructive hover:text-validation-destructive',
                        isFailed &&
                          'text-validation-warning hover:text-validation-warning-hover'
                      )}
                      onClick={() => {
                        onChange(value.filter((i) => i !== item));
                      }}
                      size={'xxs'}
                      iconOnly
                    >
                      <div className="size-3">
                        <X size={12} />
                      </div>
                    </Button>
                  </Badge>
                </TooltipTrigger>
                {isFailed && (
                  <TooltipContent>
                    <TypographyCaption>
                      <FormattedMessage
                        defaultMessage="Email is already in team"
                        id="xEFmHjOIQL"
                        description="Tooltip explaining that an entered email belongs to the team already"
                      />
                    </TypographyCaption>
                    <TooltipArrow />
                  </TooltipContent>
                )}
                {isInvalid && (
                  <TooltipContent>
                    <TypographyCaption>
                      <FormattedMessage
                        defaultMessage="Invalid email format"
                        id="9INcp9Oqmz"
                        description="Tooltip explaining that an entered email address is malformed"
                      />
                    </TypographyCaption>
                    <TooltipArrow />
                  </TooltipContent>
                )}
              </Tooltip>
            );
          })}
          <Input
            wrapperClassName="border-none flex-1 min-w-[100px]"
            className="max-h-5 text-[0.75rem]"
            data-testid="email-address-input"
            aria-label={intl.formatMessage({
              defaultMessage: 'Email address input',
              id: 'asuHCDpbwl',
              description: 'Accessible label for the input used to add email addresses',
            })}
            value={pendingDataPoint}
            onChange={(e) => setPendingDataPoint(e.target.value)}
            onBlur={addPendingDataPoint}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                addPendingDataPoint();
              } else if (
                e.key === 'Backspace' &&
                pendingDataPoint.length === 0 &&
                value.length > 0
              ) {
                e.preventDefault();
                onChange(value.slice(0, -1));
              }
            }}
            disabled={!!isLimitReached}
            placeholder={value.length > 0 ? undefined : placeholder}
            {...props}
            ref={ref}
          />
        </div>
        {isLimitReached && (
          <div className="mt-1 flex items-center gap-1 text-xs text-validation-destructive">
            <TriangleAlert size={12} />
            <TypographyCaptionBold>
              <FormattedMessage
                defaultMessage="{limit, plural, one {Limit # email address} other {Limit # email addresses}}"
                id="wtCDm/2xkz"
                description="Warning shown when the maximum number of email addresses has been entered"
                values={{ limit: limit ?? 0 }}
              />
            </TypographyCaptionBold>
          </div>
        )}
      </div>
    );
  }
);

InputEmails.displayName = 'InputEmails';

export { InputEmails };
